/**
 * Service d'authentification
 */

import api from './api.js';

/**
 * Inscription d'un nouvel utilisateur
 * Version 3.4: Stocke automatiquement le token et les infos utilisateur après inscription réussie
 */
export async function register(userData) {
  const response = await api.post('/auth/register', userData);
  
  // Stocker le token et les infos utilisateur après inscription réussie
  if (response.data.success && response.data.data) {
    if (response.data.data.token) {
      // Store both for compatibility
      localStorage.setItem('token', response.data.data.token);
      localStorage.setItem('authToken', response.data.data.token);
      if (response.data.data.refreshToken) {
        localStorage.setItem('refreshToken', response.data.data.refreshToken);
      }
      if (response.data.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.data.user));
      }
    }
  }
  
  return response.data;
}

/**
 * Connexion d'un utilisateur
 */
export async function login(telephone, password) {
  const response = await api.post('/auth/login', {
    telephone,
    password,
  });
  
  // Stocker le token et les infos utilisateur
  if (response.data.success && response.data.data) {
    // Store both for compatibility
    localStorage.setItem('token', response.data.data.token);
    localStorage.setItem('authToken', response.data.data.token);
    if (response.data.data.refreshToken) {
      localStorage.setItem('refreshToken', response.data.data.refreshToken);
    }
    if (response.data.data.user) {
      localStorage.setItem('user', JSON.stringify(response.data.data.user));
    }
    const idleMins = response.data.data.sessionIdleTimeoutMinutes;
    if (idleMins !== undefined && idleMins !== null) {
      const parsed = parseInt(String(idleMins), 10);
      if (!Number.isNaN(parsed) && parsed > 0) {
        sessionStorage.setItem('sessionIdleTimeoutMinutes', String(parsed));
      } else {
        sessionStorage.removeItem('sessionIdleTimeoutMinutes');
      }
    }
  }
  
  return response.data;
}

/**
 * Déconnexion - Clear all session data
 */
export function logout() {
  // Clear all auth-related localStorage items
  localStorage.removeItem('token');
  localStorage.removeItem('authToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  
  // Clear any other potential session storage
  sessionStorage.clear();
  
  // Clear any cached user preferences that might be tied to the session
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    // Remove any keys that might be user-specific or session-related
    if (key && (
      key.startsWith('parrain_sympa_niveau_') ||
      key.startsWith('user_') ||
      key.startsWith('session_') ||
      key.startsWith('auth_')
    )) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
}

/**
 * Rafraîchir le token
 */
export async function refreshToken() {
  const storedRefreshToken = localStorage.getItem('refreshToken');
  if (!storedRefreshToken) {
    throw new Error('No refresh token available');
  }
  
  const response = await api.post('/auth/refresh', {
    refreshToken: storedRefreshToken,
  });
  
  if (response.data.success && response.data.data) {
    // Store both for compatibility
    localStorage.setItem('token', response.data.data.token);
    localStorage.setItem('authToken', response.data.data.token);
    if (response.data.data.refreshToken) {
      localStorage.setItem('refreshToken', response.data.data.refreshToken);
    }
  }
  
  return response.data;
}

/**
 * Récupérer la configuration publique du formulaire d'inscription.
 * Indique notamment si la vérification OTP est obligatoire (otpRequired).
 */
export async function getRegistrationConfig() {
  const response = await api.get('/meta/registration');
  return response.data;
}

/**
 * Demander un code de vérification téléphone (inscription).
 * Le canal (SMS ou appel vocal) dépend du pays sélectionné ; repli possible après échecs.
 * @param {string} telephone
 * @param {{ countryCode: string, channel?: 'sms' | 'voice', userType: string }} opts
 */
export async function sendPhoneCode(telephone, { countryCode, channel, userType } = {}) {
  const response = await api.post('/auth/send-phone-code', {
    telephone,
    country_code: countryCode,
    user_type: userType,
    ...(channel ? { channel } : {}),
  });
  return response.data;
}

/**
 * Repli email : demander un code de vérification par email
 * (utilisé quand l'appel vocal n'aboutit pas).
 */
export async function sendEmailCode(telephone, email) {
  const response = await api.post('/auth/send-email-code', { telephone, email });
  return response.data;
}

/**
 * Vérifier le code OTP et obtenir phone_verification_token
 * @param {string} telephone
 * @param {string} code
 * @param {{ countryCode?: string }} [opts]
 */
export async function verifyPhoneCode(telephone, code, { countryCode } = {}) {
  const response = await api.post('/auth/verify-phone-code', {
    telephone,
    code,
    ...(countryCode ? { country_code: countryCode } : {}),
  });
  return response.data;
}

/**
 * F-26 — preuve SIM tardive (compte inscrit via le repli email) : code SMS/appel
 * envoyé au numéro DU COMPTE (authentifié ; jamais un numéro fourni ici, jamais email).
 */
export async function sendReverifyCode({ channel } = {}) {
  const response = await api.post('/auth/phone/send-code', channel ? { channel } : {});
  return response.data;
}

/**
 * F-26 — un code SMS/appel valide lève phone_verification_pending (argent débloqué).
 */
export async function verifyReverifyCode(code) {
  const response = await api.post('/auth/phone/verify-code', { code });
  return response.data;
}

/**
 * Changer le mot de passe
 */
export async function changePassword(oldPassword, newPassword) {
  const response = await api.post('/auth/change-password', {
    oldPassword,
    newPassword,
  });
  // F-29 : le changement révoque toutes les sessions ; le serveur renvoie une
  // nouvelle paire de jetons pour que cet appareil reste connecté.
  const data = response.data?.data;
  if (response.data?.success && data?.token) {
    localStorage.setItem('token', data.token);
    localStorage.setItem('authToken', data.token);
    if (data.refreshToken) {
      localStorage.setItem('refreshToken', data.refreshToken);
    }
  }
  return response.data;
}

/**
 * Réinitialiser le mot de passe (après vérification OTP)
 */
export async function sendPasswordResetCode(telephone, { countryCode, channel } = {}) {
  const response = await api.post('/auth/forgot-password/send-code', {
    telephone,
    country_code: countryCode,
    ...(channel ? { channel } : {}),
  });
  return response.data;
}

/**
 * Vérifier le code OTP de réinitialisation
 */
export async function verifyPasswordResetCode(telephone, code, { countryCode } = {}) {
  const response = await api.post('/auth/forgot-password/verify-code', {
    telephone,
    code,
    ...(countryCode ? { country_code: countryCode } : {}),
  });
  return response.data;
}

/**
 * Réinitialiser le mot de passe
 */
export async function resetPassword(telephone, passwordResetToken, newPassword, confirmPassword) {
  const response = await api.post('/auth/reset-password', {
    telephone,
    password_reset_token: passwordResetToken,
    newPassword,
    confirmPassword,
  });
  return response.data;
}

/**
 * Obtenir les informations de l'utilisateur connecté
 */
export async function getMe() {
  const response = await api.get('/auth/me');
  return response.data;
}

/**
 * Vérifier si l'utilisateur est connecté
 */
export function isAuthenticated() {
  return !!localStorage.getItem('authToken');
}

/**
 * Obtenir le token
 */
export function getToken() {
  return localStorage.getItem('authToken');
}

/**
 * Obtenir l'utilisateur stocké
 */
export function getStoredUser() {
  const userStr = localStorage.getItem('user');
  if (!userStr) {
    return null;
  }
  
  try {
    return JSON.parse(userStr);
  } catch (_error) {
    // Remove corrupted data from localStorage
    localStorage.removeItem('user');
    return null;
  }
}

/**
 * Politique de session (délai d'inactivité, public).
 * @returns {Promise<{ success: boolean, data?: { idleTimeoutMinutes: number } }>}
 */
export async function getSessionPolicy() {
  const response = await api.get('/auth/session-policy');
  return response.data;
}
